export interface TextMatch {
  start: number
  end: number
  score: number
  exact: boolean
}

interface NormalizedIndex {
  text: string
  sourceIndexes: number[]
}

const MIN_FUZZY_TARGET_LENGTH = 24
const DEFAULT_MIN_SCORE = 0.82

export function normalizePdfSearchText(value: string): string {
  return buildNormalizedIndex(value).text
}

export function findPdfTextMatch(source: string, target: string, minScore = DEFAULT_MIN_SCORE): TextMatch | null {
  const sourceIndex = buildNormalizedIndex(source)
  const targetText = normalizePdfSearchText(target)
  if (!sourceIndex.text || !targetText) return null

  const exactIdx = sourceIndex.text.indexOf(targetText)
  if (exactIdx !== -1) {
    return toSourceMatch(sourceIndex, exactIdx, exactIdx + targetText.length, 1, true)
  }

  if (targetText.length < MIN_FUZZY_TARGET_LENGTH || sourceIndex.text.length < MIN_FUZZY_TARGET_LENGTH) {
    return null
  }

  const fuzzy = findFuzzyNormalizedMatch(sourceIndex.text, targetText, minScore)
  if (!fuzzy) return null
  return toSourceMatch(sourceIndex, fuzzy.start, fuzzy.end, fuzzy.score, false)
}

function buildNormalizedIndex(value: string): NormalizedIndex {
  let text = ''
  const sourceIndexes: number[] = []

  for (let i = 0; i < value.length; i++) {
    const normalized = value[i]!
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()

    for (const char of normalized) {
      if (/[a-z0-9]/.test(char)) {
        text += char
        sourceIndexes.push(i)
      }
    }
  }

  return { text, sourceIndexes }
}

function toSourceMatch(index: NormalizedIndex, normalizedStart: number, normalizedEnd: number, score: number, exact: boolean): TextMatch | null {
  if (normalizedStart < 0 || normalizedEnd <= normalizedStart) return null
  const start = index.sourceIndexes[normalizedStart]
  const last = index.sourceIndexes[normalizedEnd - 1]
  if (start == null || last == null) return null
  return { start, end: last + 1, score, exact }
}

function findFuzzyNormalizedMatch(source: string, target: string, minScore: number): { start: number; end: number; score: number } | null {
  const targetTrigrams = trigramCounts(target)
  const targetLength = target.length
  const minLength = Math.max(MIN_FUZZY_TARGET_LENGTH, Math.floor(targetLength * 0.72))
  const maxLength = Math.min(source.length, Math.ceil(targetLength * 1.28))
  const step = Math.max(1, Math.floor(targetLength / 12))

  let best: { start: number; end: number; score: number } | null = null

  for (let length = minLength; length <= maxLength; length += step) {
    for (let start = 0; start + length <= source.length; start += step) {
      const candidate = source.slice(start, start + length)
      const score = diceScore(targetTrigrams, trigramCounts(candidate))
      if (!best || score > best.score) {
        best = { start, end: start + length, score }
      }
    }
  }

  return best && best.score >= minScore ? best : null
}

function trigramCounts(value: string): Map<string, number> {
  const counts = new Map<string, number>()
  if (value.length <= 3) {
    counts.set(value, 1)
    return counts
  }
  for (let i = 0; i <= value.length - 3; i++) {
    const gram = value.slice(i, i + 3)
    counts.set(gram, (counts.get(gram) || 0) + 1)
  }
  return counts
}

function diceScore(a: Map<string, number>, b: Map<string, number>): number {
  let intersection = 0
  let aTotal = 0
  let bTotal = 0

  for (const [gram, count] of a) {
    aTotal += count
    intersection += Math.min(count, b.get(gram) || 0)
  }
  for (const count of b.values()) bTotal += count

  if (aTotal === 0 || bTotal === 0) return 0
  return (2 * intersection) / (aTotal + bTotal)
}
